import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import FileUpload from "../../components/FileUpload";
import VideoSourceSelector from "../../components/VideoSourceSelector";

const empty = {
  title: "", description: "", genre: "", category: "", releaseYear: 2025, rating: 0, duration: "",
  posterUrl: "", bannerUrl: "", trailerUrl: "", videoUrl: "", videoSource: "direct", embedCode: "",
  cast: "", isFeatured: false, isKidsContent: false,
};

export default function AdminMovies() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/movies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setEdit(item ? {
      ...item,
      genre: (item.genre || []).join(", "),
      cast: (item.cast || []).join(", "),
    } : { ...empty });
  };

  const save = async () => {
    const payload = {
      ...edit,
      genre: edit.genre.split(",").map((s) => s.trim()).filter(Boolean),
      cast: edit.cast.split(",").map((s) => s.trim()).filter(Boolean),
      releaseYear: parseInt(edit.releaseYear) || 2025,
      rating: parseFloat(edit.rating) || 0,
    };
    try {
      if (edit.id) await api.put(`/admin/movies/${edit.id}`, payload);
      else await api.post("/admin/movies", payload);
      toast.success(edit.id ? "Movie updated" : "Movie created");
      setEdit(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this movie?")) return;
    await api.delete(`/admin/movies/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = items.filter((i) => i.title.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="admin-movies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>Movies</h1>
          <p className="text-[#888] text-sm">{items.length} titles in library</p>
        </div>
        <button onClick={() => open(null)} className="btn-primary" data-testid="add-movie-btn">
          <Plus size={16} /> Add Movie
        </button>
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search movies..." className="w-full mb-5 px-4 py-2.5 bg-[#0d0d0d] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]" data-testid="movie-search" />

      <div className="bg-[#0d0d0d] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#080808] text-[#888] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Poster</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Year</th>
              <th className="text-left p-3">Rating</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Flags</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-3">{m.posterUrl && <img src={m.posterUrl} alt="" className="w-10 h-14 object-cover rounded" />}</td>
                <td className="p-3 font-medium">{m.title}</td>
                <td className="p-3 text-[#aaa]">{m.releaseYear}</td>
                <td className="p-3 text-[#aaa]">{m.rating?.toFixed(1)}</td>
                <td className="p-3 text-[#aaa]">{m.category}</td>
                <td className="p-3">
                  {m.isFeatured && <span className="text-[10px] bg-[#E50914]/20 text-[#E50914] px-1.5 py-0.5 rounded mr-1">FEATURED</span>}
                  {m.isKidsContent && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">KIDS</span>}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => open(m)} className="text-[#aaa] hover:text-white p-1" data-testid={`edit-movie-${m.id}`}><Pencil size={16} /></button>
                  <button onClick={() => del(m.id)} className="text-[#aaa] hover:text-[#E50914] p-1 ml-1" data-testid={`delete-movie-${m.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && <EditModal edit={edit} setEdit={setEdit} save={save} />}
    </div>
  );
}

function EditModal({ edit, setEdit, save }) {
  const f = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="edit-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{edit.id ? "Edit Movie" : "Add Movie"}</h2>
          <button onClick={() => setEdit(null)} className="text-[#aaa] hover:text-white" data-testid="close-edit"><X /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Title" value={edit.title} onChange={(v) => f("title", v)} />
          <Field label="Description" value={edit.description} onChange={(v) => f("description", v)} textarea />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Year" value={edit.releaseYear} onChange={(v) => f("releaseYear", v)} type="number" />
            <Field label="Rating (0-10)" value={edit.rating} onChange={(v) => f("rating", v)} type="number" step="0.1" />
            <Field label="Duration (e.g. 1h 45m)" value={edit.duration} onChange={(v) => f("duration", v)} />
            <Field label="Category" value={edit.category} onChange={(v) => f("category", v)} />
          </div>
          <Field label="Genres (comma-separated)" value={edit.genre} onChange={(v) => f("genre", v)} />
          <Field label="Cast (comma-separated)" value={edit.cast} onChange={(v) => f("cast", v)} />
          <FileUpload label="Poster" value={edit.posterUrl} onChange={(v) => f("posterUrl", v)} accept="image/*" kind="image" />
          <FileUpload label="Banner (wide)" value={edit.bannerUrl} onChange={(v) => f("bannerUrl", v)} accept="image/*" kind="image" />
          <Field label="Trailer URL" value={edit.trailerUrl} onChange={(v) => f("trailerUrl", v)} />
          <VideoSourceSelector
            source={edit.videoSource}
            videoUrl={edit.videoUrl}
            embedCode={edit.embedCode}
            onChange={(p) => setEdit({ ...edit, ...p })}
          />
          <div className="flex gap-6">
            <Toggle label="Featured" checked={edit.isFeatured} onChange={(v) => f("isFeatured", v)} />
            <Toggle label="Kids-safe" checked={edit.isKidsContent} onChange={(v) => f("isKidsContent", v)} />
          </div>
        </div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <button onClick={() => setEdit(null)} className="btn-secondary !py-2 !px-4">Cancel</button>
          <button onClick={save} className="btn-primary !py-2 !px-4" data-testid="save-edit-btn"><Save size={16} /> Save</button>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", textarea, step }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows="3" className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]" />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-sm focus:outline-none focus:border-[#E50914]" />
      )}
    </div>
  );
}

export function Toggle({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#E50914]" />
      <span className="text-sm">{label}</span>
    </label>
  );
}
